import { css } from '@emotion/core';
import range from 'lodash/range';
import React, { useMemo } from 'react';

export interface GridProps {
    className?: string;
    /**
     * What container the grid should use.
     */
    as?: keyof JSX.IntrinsicElements | React.ComponentType<{}>;
    /**
     * The minimum width of an item, if an item would be shrunk
     * smaller than this, the grid will subtract a column.
     */
    minItemWidth: number | string;
    /**
     * The minimum height of an item.
     */
    minItemHeight?: number | string;
    /**
     * The maximum number of columns to render, on smaller view widths
     * less columns will be shown.
     */
    maxColumns?: number;
    /**
     * The gap between columns, if a `number` is specified it will
     * be used as `px`
     */
    columnGap?: string | number;
    /**
     * The gap between rows, if a `number` is specified it will
     * be used as `px`
     */
    rowGap?: string | number;
    /**
     * Whether to allow children to expand to fill all available space
     * when there are less children than {@link maxColumns}
     */
    expand?: boolean;
}

/**
 * Handles number values for CSS
 */
function orPx(value: string | number): string {
    if (typeof value === 'number') {
        return `${value}px`;
    }
    return value;
}

const defaultRowGap = '1.5rem';
const defaultColumnGap = '2rem';

const grid = css`
    display: grid;
    row-gap: ${defaultRowGap};
    column-gap: ${defaultColumnGap};

    ul {
        padding: 0;
        margin: 0;
    }
`;

/**
 * A flexible, responsive grid component.
 */
export const Grid: React.FC<GridProps> = ({
    as: Component = 'div',
    children,
    className,
    maxColumns = 3,
    columnGap,
    rowGap,
    minItemWidth,
    minItemHeight,
    expand,
}) => {
    const minColumns = React.Children.count(children);
    const numberOfSpacers = useMemo(() => {
        if (!expand && minColumns < maxColumns) return maxColumns - minColumns;

        return 0;
    }, [expand, maxColumns, minColumns]);

    const gridItemMaxWidth = useMemo(() => {
        const gapCount = maxColumns - 1;
        const totalGapWidth = `(${gapCount} * ${orPx(columnGap ?? defaultColumnGap)})`;

        return `calc((100% - ${totalGapWidth}) / ${maxColumns})`;
    }, [maxColumns, columnGap]);

    return (
        <Component
            className={className}
            css={grid}
            style={{
                gridTemplateRows: minItemHeight
                    ? `repeat(auto-fill, minmax(${orPx(minItemHeight)}, max-content))`
                    : undefined,
                gridTemplateColumns: `repeat(auto-fit, minmax(max(${orPx(
                    minItemWidth,
                )}, ${gridItemMaxWidth}), 1fr))`,

                ...(rowGap != null ? { rowGap } : null),
                ...(columnGap != null ? { columnGap } : null),
            }}
        >
            {children}
            <>
                {numberOfSpacers > 0
                    ? range(0, numberOfSpacers).map((_, i) => {
                          return <div key={`spacer-${i}`} aria-hidden />;
                      })
                    : null}
            </>
        </Component>
    );
};
